import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  ChevronRight,
  Loader2,
  AlertCircle,
  Share2,
  MessageSquare,
} from "lucide-react";
import type { MekkeBookletSpotDto } from "@tabipla/shared";
import { Button } from "@tabipla/ui/components/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tabipla/ui/components/alert-dialog";
import { toast } from "sonner";
import { MekkeLayout } from "../layouts/MekkeLayout";
import { useBooklet, useDeleteBooklet } from "../../application/hooks/useBooklets";
import { getCategoryById } from "../../config/categories";
import { AddSpotsDialog } from "../components/booklets/AddSpotsDialog";
import { ShareDialog } from "../components/booklets/ShareDialog";
import { EditSpotCommentDialog } from "../components/booklets/EditSpotCommentDialog";
import {
  getVisibilityConfig,
  getCategoryGradient,
  canShareBooklet,
} from "../../utils/style-config";

export function BookletDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: booklet, isLoading, error } = useBooklet(id);
  const deleteMutation = useDeleteBooklet();
  const [addSpotsOpen, setAddSpotsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingSpot, setEditingSpot] = useState<MekkeBookletSpotDto | null>(null);

  // Get existing spot IDs for the dialog
  const existingSpotIds = booklet?.spots?.map((bs) => bs.spotId) ?? [];

  const canShare = canShareBooklet(booklet?.visibility, booklet?.shareCode);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t("booklets.deleted"));
      navigate("/booklets");
    } catch {
      toast.error(t("common.error"));
    }
  };

  const config = getVisibilityConfig(booklet?.visibility);
  const VisibilityIcon = config.icon;

  if (isLoading) {
    return (
      <MekkeLayout>
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MekkeLayout>
    );
  }

  if (error || !booklet) {
    return (
      <MekkeLayout>
        <div className="flex min-h-screen flex-col items-center justify-center px-4">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-10 w-10 text-destructive" />
          </div>
          <h2 className="mb-2 text-lg font-semibold">{t("common.error")}</h2>
          <p className="mb-6 text-center text-sm text-muted-foreground">
            {t("error.failedToFetch")}
          </p>
          <Button variant="outline" onClick={() => navigate("/booklets")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")}
          </Button>
        </div>
      </MekkeLayout>
    );
  }

  return (
    <MekkeLayout
      fabAction={{
        icon: Plus,
        label: t("booklets.addSpot"),
        onClick: () => setAddSpotsOpen(true),
      }}
    >
      <div className="min-h-screen pb-24">
        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("booklets.delete")}</AlertDialogTitle>
              <AlertDialogDescription>{t("booklets.deleteConfirm")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("common.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="mx-auto max-w-4xl">
          {/* Cover Image */}
          <div className="relative h-48 overflow-hidden sm:h-64">
            {booklet.coverImageUrl ? (
              <img
                src={booklet.coverImageUrl}
                alt={booklet.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="relative h-full w-full" style={{ background: config.gradientStyle }}>
                {/* Decorative circles */}
                <div
                  className="absolute -right-8 -top-8 h-32 w-32 rounded-full"
                  style={{ backgroundColor: config.decorColor1 }}
                />
                <div
                  className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full"
                  style={{ backgroundColor: config.decorColor2 }}
                />
                <div
                  className="absolute bottom-12 right-12 h-16 w-16 rounded-full"
                  style={{ backgroundColor: config.decorColor3 }}
                />
                {/* Spot count indicator */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-0.5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                    <span className="text-5xl font-extrabold">{booklet.spotCount}</span>
                    <span className="text-sm font-bold uppercase tracking-widest">spots</span>
                  </div>
                </div>
              </div>
            )}

            {/* Header buttons overlaid on cover */}
            <div className="absolute inset-x-0 top-0 flex items-center justify-between px-3 py-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="h-9 w-9 rounded-full bg-black/30 text-white backdrop-blur-sm hover:bg-black/40"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="sr-only">{t("common.back")}</span>
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  className="h-9 w-9 rounded-full bg-black/30 text-white backdrop-blur-sm hover:bg-black/40"
                >
                  <Link to={`/booklets/${id}/edit`}>
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">{t("booklets.edit")}</span>
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteDialogOpen(true)}
                  className="h-9 w-9 rounded-full bg-black/30 text-white backdrop-blur-sm hover:bg-black/40"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">{t("booklets.delete")}</span>
                </Button>
              </div>
            </div>

            {/* Visibility badge */}
            <div
              className={`absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm ${config.badgeBg}`}
            >
              <VisibilityIcon className="h-3 w-3" />
              <span>{t(`booklets.visibility.${booklet.visibility}`)}</span>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 pt-6">
            {/* Title */}
            <h1 className="text-2xl font-bold">{booklet.title}</h1>
            {booklet.description && (
              <p className="mt-2 text-muted-foreground">{booklet.description}</p>
            )}

            {/* Spot count - only if cover image exists (otherwise shown in cover) */}
            {booklet.coverImageUrl && (
              <div className="mt-3 text-sm text-muted-foreground">
                {booklet.spotCount > 0
                  ? t("booklets.spots", { count: booklet.spotCount })
                  : t("booklets.noSpots")}
              </div>
            )}

            {/* Action buttons */}
            {canShare && (
              <div className="mt-4">
                <ShareDialog
                  shareCode={booklet.shareCode!}
                  title={booklet.title}
                  description={booklet.description}
                  trigger={
                    <Button variant="outline" className="w-full gap-2">
                      <Share2 className="h-4 w-4" />
                      {t("booklets.share")}
                    </Button>
                  }
                />
              </div>
            )}

            {/* Spots List */}
            <div className="mt-6">
              <h2 className="font-semibold">{t("nav.spots")}</h2>

              {booklet.spots && booklet.spots.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {booklet.spots.map((bookletSpot) => {
                    const spot = bookletSpot.spot;
                    const category = getCategoryById(spot.category);
                    const CategoryIcon = category.icon;

                    return (
                      <div
                        key={bookletSpot.id}
                        className="flex items-center gap-3 rounded-xl border bg-card p-3 shadow-sm"
                      >
                        <Link
                          to={`/spots/${spot.id}`}
                          className="flex flex-1 items-center gap-3 transition-opacity hover:opacity-80 active:opacity-60"
                        >
                          {spot.images.length > 0 ? (
                            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg">
                              <img
                                src={spot.images[0].url}
                                alt={spot.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : (
                            <div
                              className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg"
                              style={{
                                background: getCategoryGradient(spot.category).gradient,
                              }}
                            >
                              {/* Decorative circle */}
                              <div
                                className="absolute -right-2 -top-2 h-6 w-6 rounded-full"
                                style={{
                                  backgroundColor: getCategoryGradient(spot.category).decor1,
                                }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <CategoryIcon className="h-6 w-6 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]" />
                              </div>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h3 className="font-medium">{spot.name}</h3>
                            {bookletSpot.comment ? (
                              <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">
                                {bookletSpot.comment}
                              </p>
                            ) : (
                              <p className="mt-0.5 text-sm italic text-muted-foreground/50">
                                {t("booklets.noComment")}
                              </p>
                            )}
                          </div>
                        </Link>
                        <button
                          onClick={() => setEditingSpot(bookletSpot)}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </button>
                        <Link to={`/spots/${spot.id}`}>
                          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                        </Link>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-4 rounded-lg border border-dashed p-8 text-center">
                  <p className="text-sm text-muted-foreground">{t("booklets.noSpots")}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setAddSpotsOpen(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("booklets.addSpot")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Spots Dialog */}
        {id && (
          <AddSpotsDialog
            bookletId={id}
            existingSpotIds={existingSpotIds}
            open={addSpotsOpen}
            onOpenChange={setAddSpotsOpen}
          />
        )}

        {/* Edit Spot Comment Dialog */}
        {id && (
          <EditSpotCommentDialog
            bookletId={id}
            bookletSpot={editingSpot}
            open={!!editingSpot}
            onOpenChange={(open) => !open && setEditingSpot(null)}
          />
        )}
      </div>
    </MekkeLayout>
  );
}
